import React, {Dispatch, MouseEventHandler, useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useTranslation} from 'react-i18next';
import APIServer from '@taopix/taopix-core/dist/types/Service/APIServer';
import {
  ActionType,
  AssetMetaInterface,
  CoreAsset,
  DesignerSettings,
  AssetService,
  ProjectAssetLink,
} from '@taopix/taopix-core/dist/types/types';
import {AuthError} from './Auth/ServiceAuth';
import {AssetLoadResponseAdapter, AssetStatus, CoreActions} from '@taopix/taopix-core';
import classNames from 'classnames';
import {
  Button,
  CheckCircleIcon,
  HelpTip,
  LoadingLosenge,
  Horizontal,
  Vertical,
  ThemeName,
  Theme,
} from '@taopix/taopix-design-system';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import './UploadFromDeviceDialog.scss';

export type UploadInfoResponse = {
  qrCode: string;
  uploadLink: string;
  error: boolean;
};

export type UploadFromDeviceDialogProps = {
  isOpen: boolean;
  closeEvent: MouseEventHandler;
  assetService: AssetService;
  designerSettings: DesignerSettings;
  server: APIServer;
  projectImageList: AssetMetaInterface[];
};

type StepPanelTypes = {highlighted: boolean; stepNumber: string; children: any};

const UploadFromDeviceDialog = ({
  isOpen,
  closeEvent,
  assetService,
  designerSettings,
  server,
  projectImageList,
  ...props
}: UploadFromDeviceDialogProps) => {
  const {t} = useTranslation(['uploadexternalassets']);
  const ui = useUiState();

  const [, setErrorData] = useState<AuthError>();
  const [loading, setLoading] = useState<boolean>(false);
  const [imageCount, setImageCount] = useState<number>(0);
  const [tipOpen, setTipOpen] = useState<boolean>(false);
  const [uploadInfo, setUploadInfo] = useState<UploadInfoResponse>({
    qrCode: '',
    uploadLink: '',
    error: false,
  });

  const intervalRef = useRef<NodeJS.Timeout>();
  const assetsRef = useRef<AssetMetaInterface[]>(projectImageList);

  const dispatch = useDispatch<Dispatch<ActionType<typeof CoreActions>>>();

  const onError = (data: any) => {
    setErrorData(data);
    setLoading(true);
  };

  const onAssetsReceived = (response: any) => {
    const data = Object.values(response.data);
    if (data.length > 0) {
      const adapted = AssetLoadResponseAdapter(data as ProjectAssetLink[], designerSettings);
      // loop through the assets and add to the status checker if they are not processed/errored
      const complete = adapted.filter(a => {
        if ([AssetStatus.Processed, AssetStatus.Error].includes(a.status)) {
          return true;
        }
        return false;
      });

      if (complete.length) {
        setImageCount(current => current + complete.length);
        dispatch(CoreActions.addAssets(complete));
        assetsRef.current = [...assetsRef.current, ...complete];
      }
    }
  };

  const onRequestAssets = (assets: CoreAsset[]) => {
    server
      .post<UploadInfoResponse>(
        '/api/assetservice/getNewAssetsUploadFromDevice',
        {
          knownAssetIds: assets.map(asset => {
            return asset.id;
          }),
        },
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          token: 'token',
        }
      )
      .then(({data}) => onAssetsReceived(data))
      .catch(error => onError(error));
  };

  const getUploadInfo = () => {
    if (uploadInfo.qrCode === '' && loading === false) {
      setLoading(true);
      server
        .post<UploadInfoResponse>(
          '/api/assetservice/uploadFromDevice',
          {},
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            token: 'token',
          }
        )
        .then(({data}) => setUploadInfo(data))
        .catch(error => onError(error));
    }
  };

  const onCloseDialog = () => {
    server.post<{alive: boolean}>(
      '/api/assetservice/token/revoke',
      {},
      {
        token: 'token',
        json: true,
      }
    );
  };

  const copyLink = () => {
    if (navigator.share && navigator.maxTouchPoints > 0) {
      // Show only on mobile devices.
      navigator
        .share({
          title: t('str_TitleExternalUploadPictureUploader', {
            ns: 'uploadexternalassets',
          }),
          url: uploadInfo.uploadLink,
        })
        .then(() => console.log('Successful share'))
        .catch(error => console.log('Error sharing:', error));
    } else if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(uploadInfo.uploadLink).then(() => {
        setTipOpen(true);
      });
    }
    return false;
  };

  useEffect(() => {
    getUploadInfo();

    return () => {
      onCloseDialog();
    };
  }, []);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      onRequestAssets(assetsRef.current);
    }, 5000);

    return () => {
      clearInterval(intervalRef.current);
    };
  }, []);

  const PictureCountMessage = () => {
    let message;
    switch (imageCount) {
      case 0:
        message = t('str_MessageImagesCheckingForImages', {
          ns: 'uploadexternalassets',
        });
        break;

      case 1:
        message = t('str_MessageImageUploaded', {
          ns: 'uploadexternalassets',
        });
        break;

      default:
        message = t('str_MessageImagesUploaded', {
          ns: 'uploadexternalassets',
        }).replace('^0', imageCount.toString());
        break;
    }

    return <p className={classNames(imageCount > 0 && 'sm:text-[0px]')}>{message}</p>;
  };

  const panelListClasses = classNames(
    'flex',
    'sm:flex-col',
    'items-stretch',
    'lg:space-x-md',
    'list-none',
    'p-0 m-0'
  );
  const listItemClasses = classNames('flex', 'flex-1', 'mt-lg');

  const panelClasses = classNames(
    'flex',
    'flex-col',
    'w-full',
    'h-full',
    'items-center',
    'lg:text-center',
    'text-[14rem]',
    'leading-[1.2]',
    'relative',
    'py-xl',
    'px-lg'
  );

  const numberClasses = classNames(
    'absolute',
    'w-[30rem]',
    'h-[30rem]',
    'flex',
    'justify-center',
    'items-center',
    'font-bold',
    'leading-[30rem]',
    '-top-[15rem]',
    'rounded-full'
  );

  const instructionTextClasses = classNames(
    'lg:min-h-[60rem]',
    'sm:min-h-[auto]',
    'xs:text-sm',
    'leading-[1.3]'
  );

  const pictureCountClasses = classNames(
    'flex flex-col relative h-full sm:p-xs sm:text-sm text-center',
    imageCount > 0
      ? 'justify-end ufp-picture-count--animate ufp-picture-count--received'
      : 'justify-center sm:border sm:border-themeTextColour20'
  );

  const StepPanel = ({highlighted = false, stepNumber = '', children}: StepPanelTypes) => {
    return (
      <li className={listItemClasses}>
        <Theme
          name={highlighted ? ThemeName.Prominent : ThemeName.Section}
          className={panelClasses}
        >
          <Theme name={ThemeName.Primary} className={numberClasses} allowCorners={false}>
            {stepNumber}
          </Theme>
          {children}
        </Theme>
      </li>
    );
  };

  return (
    <>
      <ol className={panelListClasses}>
        <StepPanel highlighted={imageCount === 0} stepNumber={'1'}>
          <div
            className={
              'flex flex-col lg:justify-center lg:items-center lg:space-y-sm sm:self-start sm:space-y-sm sm:w-[calc(100%-120rem)] sm:min-h-[120rem] sm:items-start'
            }
          >
            <p className={instructionTextClasses}>
              {ui.state.isSmallScreen ? (
                <>
                  {t('str_MessageExternalUploadStep1Device', {
                    ns: 'uploadexternalassets',
                  })}
                </>
              ) : (
                <>
                  {t('str_MessageExternalUploadStep1Phone', {
                    ns: 'uploadexternalassets',
                  })}
                </>
              )}
            </p>
            <div className="bg-white sm:absolute sm:top-xl sm:right-lg flex justify-center lg:w-[160rem] lg:h-[160rem] sm:w-[100rem] sm:h-[100rem] sm:!m-0">
              {uploadInfo?.qrCode !== '' ? (
                <img
                  id="external-upload-qrcode"
                  className="bg-white"
                  src={'data:image/png;base64,' + uploadInfo?.qrCode}
                  alt={t('str_MessageExternalUploadStep1', {
                    ns: 'uploadexternalassets',
                  })}
                />
              ) : (
                <LoadingLosenge />
              )}
            </div>
            <Button
              label={t('str_MessageShareUploadLink', {
                ns: 'uploadexternalassets',
              })}
              id={'copyLinkButton'}
              buttonStyle={'distinct'}
              size={'small'}
              onClick={copyLink}
              disabled={uploadInfo?.qrCode === ''}
            />
          </div>
        </StepPanel>
        <StepPanel highlighted={imageCount > 0} stepNumber={'2'}>
          <div
            className={
              'flex flex-col lg:justify-center lg:items-center lg:space-y-sm sm:self-start sm:space-y-sm sm:w-[calc(100%-120rem)] sm:min-h-[120rem] sm:items-start'
            }
          >
            <p className={instructionTextClasses}>
              {t('str_MessageExternalUploadStep2', {
                ns: 'uploadexternalassets',
              })}
            </p>
            <div className="sm:absolute sm:top-xl sm:right-lg flex justify-center lg:h-[160rem] sm:w-[100rem] sm:h-[100rem] sm:!m-0">
              <div className={pictureCountClasses} data-addedcount={imageCount}>
                <PictureCountMessage />
              </div>
            </div>
          </div>
        </StepPanel>
      </ol>
      <HelpTip
        open={tipOpen}
        message={t('*:str_ToolTipLinkCopied')}
        anchorId={'copyLinkButton'}
        anchorOrigin={
          ui.state.isSmallScreen
            ? {
                vertical: Vertical.Top,
                horizontal: Horizontal.Left,
              }
            : {
                vertical: Vertical.Top,
                horizontal: Horizontal.Centre,
              }
        }
        transformOrigin={
          ui.state.isSmallScreen
            ? {
                vertical: Vertical.Bottom,
                horizontal: Horizontal.Left,
              }
            : {
                vertical: Vertical.Bottom,
                horizontal: Horizontal.Centre,
              }
        }
        arrow
        offset={{x: 0, y: -10}}
        buttonLabel={t('*:str_ButtonOK')}
        buttonFunction={() => setTipOpen(false)}
        onClose={() => setTipOpen(false)}
        icon={<CheckCircleIcon />}
        hideAfter={10000}
      />
    </>
  );
};

export default UploadFromDeviceDialog;
